<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import api from '@/api';
import Swal from 'sweetalert2';
import MembresiaFormModal from './components/MembresiaFormModal.vue';
import { hasPermission } from '@/composables/useAuth';

const canGestionarCatalogo = computed(() => hasPermission('membresias_catalogo.gestionar'));

const membresiaSeleccionada = ref(null);
const membresias = ref([]);
const modalVisible = ref(false);
const nombreFiltro = ref('');
const duracionMinFiltro = ref(0);
const pagina = ref(1);
const totalPaginas = ref(1);
const isEditando = ref(false);

const cargarMembresias = async () => {
  try {
    const res = await api.get('/membresias', {
      params: {
        page: pagina.value,
        limit: 10,
        nombre: nombreFiltro.value,
        duracionMin: duracionMinFiltro.value,
        estado: 'all', // admin necesita activas e inactivas; asignación sigue usando default solo activas
      },
    });
    membresias.value = res.data.membresias;
    totalPaginas.value = res.data.totalPages;
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.error(err);
    Swal.fire('Error', 'No se pudo cargar membresías', 'error');
  }
};

const buscar = () => {
  pagina.value = 1;
  cargarMembresias();
};

watch([nombreFiltro, duracionMinFiltro], () => {
  buscar();
});

const cambiarPagina = (nueva) => {
  if (nueva >= 1 && nueva <= totalPaginas.value) {
    pagina.value = nueva;
    cargarMembresias();
  }
};

const nuevo = () => {
  membresiaSeleccionada.value = null;
  isEditando.value = false;
  modalVisible.value = true;
};

const guardarMembresia = async (data) => {
  try {
    // El backend valida por modo (variable usa cantidad, visitas usa limite). No forzar 1 aquí.
    const payload = { ...data };
    if (isEditando.value && payload.id) {
      await api.put(`/membresias/${payload.id}`, payload);
    } else {
      await api.post('/membresias', payload);
    }
    modalVisible.value = false;
    await cargarMembresias();
    return true;
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.error('Error en guardarMembresia:', err);
    return err?.response?.data?.message || err.message || 'Error inesperado al guardar';
  }
};

const editar = (membresia) => {
  membresiaSeleccionada.value = { ...membresia };
  isEditando.value = true;
  modalVisible.value = true;
};

const eliminar = async (membresia) => {
  // FE-012: DELETE lógico (estado=false) — no borra historial (RESTRICT)
  Swal.fire({
    title: '¿Inactivar membresía?',
    text: `¿Deseas inactivar la membresía "${membresia.nombre}"? Dejará de estar disponible para nuevas asignaciones pero conserva el historial.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Sí, inactivar',
    cancelButtonText: 'Cancelar'
  }).then(async (r) => {
    if (r.isConfirmed) {
      try {
        await api.delete(`/membresias/${membresia.id}`);
        Swal.fire('Inactivada', 'Membresía inactivada correctamente', 'success');
        cargarMembresias();
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') console.error(error);
        const msg = error.response?.data?.message || 'No se pudo inactivar la membresía';
        Swal.fire('Error', msg, 'error');
      }
    }
  });
};

const reactivar = async (membresia) => {
  Swal.fire({
    title: '¿Reactivar membresía?',
    text: `¿Deseas reactivar la membresía "${membresia.nombre}"?`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Sí, reactivar',
    cancelButtonText: 'Cancelar'
  }).then(async (r) => {
    if (r.isConfirmed) {
      try {
        // Backend real: PATCH /api/membresias/membresias/:id/reactivar (ver routes/membresias.routes.js:15)
        await api.patch(`/membresias/membresias/${membresia.id}/reactivar`);
        Swal.fire('Reactivada', 'Membresía reactivada correctamente', 'success');
        cargarMembresias();
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') console.error(error);
        const msg = error.response?.data?.message || 'No se pudo reactivar la membresía';
        Swal.fire('Error', msg, 'error');
      }
    }
  });
};

onMounted(cargarMembresias);
</script>

<template>
    <div class="container-fluid py-4">
        <div class="card p-4">
            <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h5 class="mb-0">Membresías</h5>
                <div class="d-flex gap-2">
                    <button v-if="canGestionarCatalogo" class="btn btn-primary btn-sm" @click="nuevo">
                        + Nueva membresía
                    </button>
                </div>
            </div>

            <div class="row mb-3">
                <div class="col-md-5 mb-2">
                    <label for="">Buscar por Nombre</label>
                    <input v-model="nombreFiltro" placeholder="Buscar por nombre"
                        class="form-control form-control-sm" />
                </div>
                <div class="col-md-3 mb-2">
                    <label for="">Buscar por Duración</label>
                    <input v-model.number="duracionMinFiltro" type="number" min="0" placeholder="Duración mínima"
                        class="form-control form-control-sm" />
                </div>

            </div>

            <div class="table-responsive">
                <table class="table table-sm align-items-center custom-table text-sm">
                    <thead >
                        <tr>
                            <th>#</th>
                            <th>NOMBRE</th>
                            <th>DURACIÓN</th>
                            <th>PRECIO</th>
                            <th>ESTADO</th>
                            <th class="text-end">ACCIONES</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(m, i) in membresias" :key="m.id">
                            <td>{{ i + 1 + (pagina - 1) * 10 }}</td>
                            <td>{{ m.nombre }}</td>
                            <td>{{ m.duracion?.nombre || '—' }}<small v-if="m.duracion?.modo" class="text-muted"> ({{ m.duracion.modo }}{{ m.duracion.modo === 'variable' ? ': ' + m.cantidad + ' días' : m.duracion.modo === 'fijo' ? ': ' + m.duracion.dias_equivalentes + 'd' : '' }}{{ m.tipo_vencimiento === 'visitas' ? ' · ' + m.limite_asistencias + ' visitas' : '' }})</small></td>
                            <td>{{ m.precio }}</td>
                            <td><span class="badge" :class="m.estado ? 'bg-success' : 'bg-secondary'">{{ m.estado ? 'Activa' : 'Inactiva' }}</span></td>
                            <td class="text-end text-nowrap">
                                <button v-if="canGestionarCatalogo" class="btn btn-outline-secondary btn-xs me-1" title="Editar" @click="editar(m)">
                                    <i class="ni ni-settings text-secondary text-xs"></i>
                                </button>
                                <button v-if="canGestionarCatalogo && m.estado !== false" class="btn btn-outline-danger btn-xs me-1" title="Inactivar" @click="eliminar(m)">
                                    <i class="ni ni-fat-remove text-danger text-xs"></i>
                                </button>
                                <button v-if="canGestionarCatalogo && m.estado === false" class="btn btn-outline-success btn-xs me-1" title="Reactivar" @click="reactivar(m)">
                                    <i class="ni ni-check-bold text-success text-xs"></i>
                                </button>
                                <span v-if="!canGestionarCatalogo" class="text-muted small">Solo lectura</span>
                            </td>
                        </tr>
                        <tr v-if="membresias.length === 0">
                            <td colspan="6" class="text-center">No hay resultados</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="d-flex justify-content-between align-items-center mt-3 px-2">
                <small class="text-muted">Página {{ pagina }} de {{ totalPaginas }}</small>
                <div>
                    <button class="btn btn-outline-secondary btn-sm me-2" :disabled="pagina === 1"
                        @click="cambiarPagina(pagina - 1)">
                        Anterior
                    </button>
                    <button class="btn btn-outline-secondary btn-sm" :disabled="pagina === totalPaginas"
                        @click="cambiarPagina(pagina + 1)">
                        Siguiente
                    </button>
                </div>
            </div>
        </div>
        <MembresiaFormModal :model-value="membresiaSeleccionada" :visible="modalVisible" :is-edit="isEditando"
            @update:visible="modalVisible = $event" :onSave="guardarMembresia" />

    </div>
</template>
<style scoped>
.custom-table th,
.custom-table td {
  padding: 0.25rem 0.4rem !important; 
  font-size: 0.78rem !important;      
  line-height: 1.2;
}
.custom-table thead {
  background-color: #f2f2f2;
  color: #333;
  font-size: 0.8rem;
  text-transform: uppercase;
}
.custom-table tbody tr:hover {
  background-color: #f8f9fa;
}
.custom-table td {
  vertical-align: middle;
}

</style>